import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard,
  FileText,
  LogOut,
  Users,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Sun,
  Moon,
  Tag,
  X,
  Menu
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Sidebar({ user, theme, toggleTheme, handleLogout, isMobile, onClose }) {
  const location = useLocation();
  
  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link
      to={to}
      onClick={onClose}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-primary">ETELEKOM SMS</h1>
          <p className="text-xs text-muted-foreground">Mesaj Yönetim Paneli</p>
        </div>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1">
        <NavLink to="/dashboard" icon={LayoutDashboard} active={location.pathname === '/dashboard'}>
          Dashboard
        </NavLink>
        <NavLink to="/admin" icon={Users} active={location.pathname === '/admin'}>
          Kullanıcı Yönetimi
        </NavLink>
        <NavLink to="/logs" icon={FileText} active={location.pathname === '/logs'}>
          İşlem Logları
        </NavLink>
      </nav>

      <div className="p-3 border-t border-border space-y-3">
        <div className="flex items-center justify-between p-2">
          <span className="text-sm">Tema</span>
          <Button variant="outline" size="icon" onClick={toggleTheme} className="h-8 w-8">
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{user?.username}</span>
            <Badge variant="outline" className="text-xs">Admin</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 w-8 p-0">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [users, setUsers] = useState([]);
  const [basliklar, setBasliklar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newBaslik, setNewBaslik] = useState('');
  const [addingBaslik, setAddingBaslik] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user',
    assigned_basliklar: []
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchBasliklar = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/basliklar`, { headers: getAuthHeader() });
      setBasliklar(response.data.basliklar || []);
    } catch (error) {
      console.error('Basliklar fetch error:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchUsers();
    fetchBasliklar();
  }, [fetchUsers, fetchBasliklar]);

  const handleAddBaslik = async () => {
    if (!newBaslik.trim()) {
      toast.error('Başlık adı gerekli');
      return;
    }
    setAddingBaslik(true);
    try {
      await axios.post(`${API_URL}/admin/basliklar`, { name: newBaslik.trim() }, { headers: getAuthHeader() });
      toast.success('Başlık eklendi');
      setNewBaslik('');
      fetchBasliklar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Başlık eklenemedi');
    } finally {
      setAddingBaslik(false);
    }
  };

  const handleDeleteBaslik = async (baslikName) => {
    if (!confirm(`"${baslikName}" başlığını silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/basliklar/${encodeURIComponent(baslikName)}`, { headers: getAuthHeader() });
      toast.success('Başlık silindi');
      fetchBasliklar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Başlık silinemedi');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error('Kullanıcı adı ve şifre gerekli');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/users`, newUser, { headers: getAuthHeader() });
      toast.success('Kullanıcı oluşturuldu');
      setCreateDialogOpen(false);
      setNewUser({ username: '', password: '', role: 'user', assigned_basliklar: [] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı oluşturulamadı');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API_URL}/admin/users/${selectedUser.id}`, {
        assigned_basliklar: selectedUser.assigned_basliklar
      }, { headers: getAuthHeader() });
      toast.success('Kullanıcı güncellendi');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı güncellenemedi');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı silinemedi');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleBaslik = (baslik, isNew = false) => {
    if (isNew) {
      const current = newUser.assigned_basliklar || [];
      if (current.includes(baslik)) {
        setNewUser({ ...newUser, assigned_basliklar: current.filter(b => b !== baslik) });
      } else {
        setNewUser({ ...newUser, assigned_basliklar: [...current, baslik] });
      }
    } else if (selectedUser) {
      const current = selectedUser.assigned_basliklar || [];
      if (current.includes(baslik)) {
        setSelectedUser({ ...selectedUser, assigned_basliklar: current.filter(b => b !== baslik) });
      } else {
        setSelectedUser({ ...selectedUser, assigned_basliklar: [...current, baslik] });
      }
    }
  };

  const sidebarProps = { user, theme, toggleTheme, handleLogout };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 border-r border-border bg-card/80 backdrop-blur-sm z-50">
        <Sidebar {...sidebarProps} isMobile={false} onClose={() => {}} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-card/95 backdrop-blur-sm z-50 flex items-center justify-between px-4">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <Sidebar {...sidebarProps} isMobile={true} onClose={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        <h1 className="font-heading text-lg font-bold text-primary">Kullanıcı Yönetimi</h1>
        <div className="w-10" />
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <div className="hidden lg:block">
            <h2 className="font-heading text-2xl font-semibold">Kullanıcı Yönetimi</h2>
            <p className="text-sm text-muted-foreground">Kullanıcıları yönetin ve başlık atamaları yapın</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full lg:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Yeni Kullanıcı
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Yeni Kullanıcı Oluştur</DialogTitle>
                <DialogDescription>Kullanıcı bilgilerini girin ve başlık ataması yapın</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Kullanıcı Adı</Label>
                  <Input
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="Kullanıcı adı"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Şifre</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Şifre"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Kullanıcı</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Atanacak Başlıklar</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
                    {basliklar.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Henüz başlık yok.</p>
                    ) : (
                      basliklar.map((baslik) => (
                        <Badge
                          key={baslik}
                          variant={newUser.assigned_basliklar?.includes(baslik) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleBaslik(baslik, true)}
                        >
                          {baslik}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="w-full sm:w-auto">İptal</Button>
                <Button onClick={handleCreateUser} className="w-full sm:w-auto">Oluştur</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Başlıklar */}
        <Card className="bg-card/80 mb-4 lg:mb-6">
          <CardHeader className="p-4">
            <CardTitle className="font-heading text-base lg:text-lg flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Mevcut Başlıklar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Yeni başlık adı"
                value={newBaslik}
                onChange={(e) => setNewBaslik(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddBaslik()}
                className="flex-1"
              />
              <Button onClick={handleAddBaslik} disabled={addingBaslik}>
                {addingBaslik ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {basliklar.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henüz başlık yok.</p>
              ) : (
                basliklar.map((baslik) => (
                  <Badge key={baslik} variant="secondary" className="flex items-center gap-1 pr-1">
                    {baslik}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
                      onClick={() => handleDeleteBaslik(baslik)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Kullanıcılar */}
        <Card className="bg-card/80">
          <CardHeader className="p-4">
            <CardTitle className="font-heading text-base lg:text-lg">Kullanıcılar</CardTitle>
            <CardDescription>{users.length} kullanıcı kayıtlı</CardDescription>
          </CardHeader>
          <CardContent className="p-0 lg:p-4">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Başlıklar</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.username}</TableCell>
                          <TableCell>
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                              {u.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {u.assigned_basliklar?.length > 0 ? (
                                u.assigned_basliklar.map((b) => (
                                  <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {u.role === 'admin' ? 'Tümü' : '-'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {u.role !== 'admin' && (
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(u); setEditDialogOpen(true); }}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {users.map((u) => (
                    <Card key={u.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{u.username}</span>
                          <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                            {u.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {u.assigned_basliklar?.length > 0 ? (
                            u.assigned_basliklar.map((b) => (
                              <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {u.role === 'admin' ? 'Tüm başlıklar' : 'Başlık atanmamış'}
                            </span>
                          )}
                        </div>
                        {u.role !== 'admin' && (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedUser(u); setEditDialogOpen(true); }}>
                              <Edit className="h-4 w-4 mr-1" /> Düzenle
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDeleteUser(u.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Kullanıcı Düzenle: {selectedUser?.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Atanan Başlıklar</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
                  {basliklar.map((baslik) => (
                    <Badge
                      key={baslik}
                      variant={selectedUser?.assigned_basliklar?.includes(baslik) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleBaslik(baslik)}
                    >
                      {baslik}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="w-full sm:w-auto">İptal</Button>
              <Button onClick={handleUpdateUser} className="w-full sm:w-auto">Güncelle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
